import { useMutation } from '@apollo/client'
import { toast } from 'sonner'

import {
  ENABLE_MCLEOD_API_APP,
  ENABLE_MCLEOD_APP,
  REQUEST_TMS_LOAD_SYNC,
  UPDATE_MCLEOD_API_APP
} from '../../../../data/mutations/apps'
import { GET_APPS } from '../../../../data/queries/apps'
import type { AppTypeEnum } from '../../../../types/workspace'

interface IUseMcLeodMutationsParams {
  handleClose: () => void
  setExternalWorkspaceID: (id: string) => void
  setFormError: (error: string) => void
  workspaceID: string
}

export const useMcLeodMutations = ({
  handleClose,
  setExternalWorkspaceID,
  setFormError,
  workspaceID
}: IUseMcLeodMutationsParams) => {
  const mutationHeaders = {
    context: { headers: { 'ws-id': workspaceID } },
    onError: (err: { message: string }) => {
      const errorMessage = `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`
      setFormError(errorMessage)
      toast.error(errorMessage)
    },
    refetchQueries: [GET_APPS]
  }

  const [enableMcleodApp, { loading: isEnablingDFM }] = useMutation<
    { enableMcLeodApp: { externalWorkspaceID: string } },
    {
      companyID: string
      mcLeodInstanceURL: string
      mcLeodServerTimezone: string
      mcLeodVersion: number
      password: string
      username: string
    }
  >(ENABLE_MCLEOD_APP, {
    ...mutationHeaders,
    onCompleted: (result) => {
      setExternalWorkspaceID(result.enableMcLeodApp.externalWorkspaceID)
      toast.success('McLeod DFM app enabled successfully!')
      handleClose()
    }
  })

  const [enableMcleodAPIApp, { loading: isEnablingAPI }] = useMutation<
    { enableMcLeodAPIModuleApp: { externalWorkspaceID: string } },
    { companyID: string; mcLeodInstanceURL: string; mcLeodServerTimezone: string; password: string; username: string }
  >(ENABLE_MCLEOD_API_APP, {
    ...mutationHeaders,
    onCompleted: (result) => {
      setExternalWorkspaceID(result.enableMcLeodAPIModuleApp.externalWorkspaceID)
      toast.success('McLeod API app enabled successfully!')
      handleClose()
    }
  })

  const [updateMcleodAPIApp, { loading: isUpdatingAPI }] = useMutation<
    { updateMcLeodAPIModuleApp: { message: string } },
    { companyID: string; mcLeodInstanceURL: string; mcLeodServerTimezone: string; password: string; username: string }
  >(UPDATE_MCLEOD_API_APP, {
    ...mutationHeaders,
    onCompleted: () => {
      toast.success('McLeod API app updated successfully!')
      handleClose()
    }
  })

  const [requestTMSLoadSync, { loading: isSyncing }] = useMutation(REQUEST_TMS_LOAD_SYNC, {
    context: { headers: { 'ws-id': workspaceID } },
    onCompleted: () => {
      toast.success('Load sync requested successfully')
    },
    onError: (err) => {
      toast.error(`Failed to request load sync: ${err.message}`)
    }
  })

  const handleSyncRequest = (appType: AppTypeEnum) => {
    requestTMSLoadSync({ variables: { appType } })
  }

  return {
    enableMcleodAPIApp,
    enableMcleodApp,
    handleSyncRequest,
    isLoading: isEnablingDFM || isEnablingAPI || isUpdatingAPI,
    isSyncing,
    updateMcleodAPIApp
  }
}
